import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Wallet, Check, Bell, MapPin, Palette, BarChart3 } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Toast } from '../components/Toast';
import { WalletOverviewTab } from '../components/digital-wallet/WalletOverviewTab';
import { WalletDesignTab } from '../components/digital-wallet/WalletDesignTab';
import { WalletNotificationsTab } from '../components/digital-wallet/WalletNotificationsTab';
import { WalletAutomationsTab } from '../components/digital-wallet/WalletAutomationsTab';
import { WalletLocationsTab } from '../components/digital-wallet/WalletLocationsTab';
import { WalletSettingsTab } from '../components/digital-wallet/WalletSettingsTab';
import { WalletPassPreview } from '../components/digital-wallet/WalletPassPreview';
import { walletTiers } from '../data/mockDigitalWallet';
const tabs = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'design',
  label: 'Pass design'
}, {
  id: 'notifications',
  label: 'Notifications'
}, {
  id: 'automations',
  label: 'Automations'
}, {
  id: 'locations',
  label: 'Locations'
}, {
  id: 'settings',
  label: 'Settings'
}];
function OffState({
  onTurnOn
}: {
  onTurnOn: () => void;
}) {
  const bands = [['Up to 1,000 members', '$25'], ['1,001 to 5,000', '$49'], ['5,001 to 25,000', '$89'], ['25,000+', 'Talk to us']];
  const points = [[Palette, 'Your brand on every pass, with a different look for each tier'], [BarChart3, 'Points, tier and rewards that update by themselves'], [Bell, 'Push notifications straight to the lock screen, no app needed'], [MapPin, 'The pass pops up when members are near your cellar door']] as const;
  return <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-center" data-id="element-8926" data-mp="2wd8xq71vgqp">
      <div data-id="element-8927" data-mp="vg28zl66o2es">
        <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center mb-4" data-id="element-8928" data-mp="vfsi7vdbdr81">
          <Wallet className="w-6 h-6 text-[#0284C7]" data-id="element-8929" data-mp="159o8ox52e9wj" />
        </div>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-8930" data-mp="10bcyglauxxds">Put your loyalty program in their pocket</h2>
        <p className="text-sm text-[#475569] mb-5 max-w-xl" data-id="element-8931" data-mp="vbs28in2o34r">
          WinePik digital wallets give every loyalty member a pass in Apple Wallet or Google Wallet. It shows their points, tier and rewards, and it's what your cellar door team scans.
        </p>
        <ul className="space-y-2 mb-6 list-none pl-0" data-id="element-8932" data-mp="mdy555hhh5gj">
          {points.map(([Icon, text]) => <li key={text} className="flex items-center gap-2 text-sm text-[#0F172A]" data-id="element-8933" data-mp="hjbvb6r1vkyk">
              <Icon className="w-4 h-4 text-[#0EA5E9]" data-id="element-8934" data-mp="cmlegh7f8lc3" /> {text}
            </li>)}
        </ul>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-id="element-8935" data-mp="7os7vn27hpba">
          {bands.map(([b, p]) => <div key={b} className="border border-[#E2E8F0] rounded-lg p-3" data-id="element-8936" data-mp="2nmhhvw0qe4x">
              <div className="text-xs text-[#64748B]" data-id="element-8937" data-mp="3uszseg5owvah">{b}</div>
              <div className="text-base font-semibold text-[#0F172A]" data-id="element-8938" data-mp="2a9waa8rh9k17">{p}{p.startsWith('$') && <span className="text-xs font-normal text-[#64748B]" data-id="element-8939" data-mp="2abyycn92l33a"> USD/mo</span>}</div>
            </div>)}
        </div>
        <Button variant="primary" onClick={onTurnOn} data-id="element-8940" data-mp="2jakp35op3of5">
          Turn on digital wallet
        </Button>
        <p className="text-xs text-[#94A3B8] mt-2 m-0" data-id="element-8941" data-mp="2o9f6q4axf6xo">Added to your WinePik bill. Works on any plan.</p>
      </div>
      <div className="flex justify-center" data-id="element-8942" data-mp="1qmf2sehztrd7">
        <WalletPassPreview tier={walletTiers[1]} platform="apple" compact data-id="element-8943" data-mp="1qnq8ijv0196y" />
      </div>
    </div>;
}
export function DigitalWalletPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'overview';
  const [walletOn, setWalletOn] = useState(searchParams.get('state') !== 'off');
  const [settingUp, setSettingUp] = useState(false);
  const [composeKey, setComposeKey] = useState(0);
  const [startComposing, setStartComposing] = useState(false);
  const [toast, setToast] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const showToast = (title: string) => setToast({
    id: `t-${Date.now()}`,
    title
  });
  const goTo = (target: string) => {
    if (target === 'notifications:new') {
      setStartComposing(true);
      setComposeKey(k => k + 1);
      setSearchParams({
        tab: 'notifications'
      });
      return;
    }
    setStartComposing(false);
    setSearchParams({
      tab: target
    });
  };
  const turnOn = () => {
    setSettingUp(true);
    setTimeout(() => {
      setSettingUp(false);
      setWalletOn(true);
      setSearchParams({
        tab: 'design'
      });
      showToast('Digital wallet is on. Your pass is ready to design.');
    }, 1200);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-8944" data-mp="655y70cq35xz">
      <div className="mb-6" data-id="element-8945" data-mp="2cu2f0inx1eit">
        <NavBar tabs={[]} title="Loyalty & Rewards › Digital Wallet" data-id="element-8946" data-mp="2cvdisihd2m9x" />
      </div>

      <div className="mb-6 flex items-center justify-end" data-id="element-8947" data-mp="27vr2g068kpww">
        <div className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-md p-1" data-id="element-8948" data-mp="27xc2g5bbl125">
          <span className="text-[11px] text-[#94A3B8] pl-2 pr-1 uppercase tracking-wider" data-id="element-8949" data-mp="nf9t1vqni07f">Prototype</span>
          {([['on', 'Wallet on'], ['off', 'Wallet off']] as const).map(([v, l]) => <button key={v} type="button" onClick={() => setWalletOn(v === 'on')} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${walletOn === (v === 'on') ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-id="element-8950" data-mp="ekdawdoydd7f">
              {l}
            </button>)}
        </div>
      </div>

      {!walletOn ? settingUp ? <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-16 text-center" data-id="element-8951" data-mp="3rrett4m759s5">
            <div className="w-10 h-10 mx-auto mb-4 border-4 border-[#E0F2FE] border-t-[#0EA5E9] rounded-full animate-spin" data-id="element-8952" data-mp="3vt08mviajy4z" />
            <div className="text-sm font-medium text-[#0F172A]" data-id="element-8953" data-mp="1pzsa7o4mwh5p">Setting up your wallet pass</div>
            <div className="text-xs text-[#64748B] mt-1" data-id="element-8954" data-mp="1u1wz69k50pm0">Creating a pass with your four loyalty tiers</div>
          </div> : <OffState onTurnOn={turnOn} data-id="element-8955" data-mp="32zmza0h8nsk0" /> : <>
          <div className="flex items-center gap-1 border-b border-[#E2E8F0] mb-6 overflow-x-auto" data-id="element-8956" data-mp="32w7lr237qrkp">
            {tabs.map(t => <button key={t.id} type="button" onClick={() => goTo(t.id)} className={`px-4 h-10 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tabParam === t.id ? 'border-[#0EA5E9] text-[#0284C7]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-8957" data-mp="2txvtywqm1nqo">
                {t.label}
              </button>)}
            <div className="flex-1" data-id="element-8958" data-mp="3hojag6eldlou" />
            <span className="hidden lg:inline-flex items-center gap-1.5 text-xs text-[#059669] pr-2" data-id="element-8959" data-mp="3hpktoy5rhehn">
              <Check className="w-3.5 h-3.5" data-id="element-8960" data-mp="3cuezlc27gtih" /> Passes up to date
            </span>
          </div>

          {tabParam === 'overview' && <WalletOverviewTab onGoTo={goTo} data-id="element-8961" data-mp="97eqjsuq94h7" />}
          {tabParam === 'design' && <WalletDesignTab onSaved={showToast} data-id="element-8962" data-mp="99hnyxy7lzvy" />}
          {tabParam === 'notifications' && <WalletNotificationsTab key={composeKey} startComposing={startComposing} onToast={showToast} data-id="element-8963" data-mp="4fn7e3dwi113" />}
          {tabParam === 'automations' && <WalletAutomationsTab onToast={showToast} data-id="element-8964" data-mp="gqxmt0szeu7q" />}
          {tabParam === 'locations' && <WalletLocationsTab onToast={showToast} data-id="element-8965" data-mp="gt0lsac43xol" />}
          {tabParam === 'settings' && <WalletSettingsTab onToast={showToast} onTurnOff={() => {
        setWalletOn(false);
        showToast('Digital wallet turned off');
      }} data-id="element-8966" data-mp="lsdal0mjnett" />}
        </>}

      {toast && <Toast id={toast.id} type="success" title={toast.title} duration={4000} onClose={() => setToast(null)} data-id="element-8967" data-mp="uqfgnul4wxny" />}
    </div>;
}